import { useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowDownAZ,
  ArrowUpAZ,
  Calendar1,
  CalendarSync,
  ChevronDown,
  Loader2,
  Repeat,
} from "lucide-react";
import Container from "../../components/Container.tsx";
import {
  BookingResponseDto,
  useAcceptBookingMutation,
  useTeacherBookingsDashboardQuery,
  useTeacherBookingsQuery,
} from "../../api/bookingApi.ts";
import {
  Subscription,
  useAcceptSubscriptionMutation,
  useTeacherSubscriptionsDashboardQuery,
  useTeacherSubscriptionsQuery,
} from "../../api/subscriptionApi.ts";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { LoadingSpinner } from "../../components/util/LoadingSpinner.tsx";
import PendingBookingCard from "../../components/dashboard/PendingBookingCard.tsx";
import PendingSubscriptionCard from "../../components/dashboard/PendingSubscriptionCard.tsx";
import TeacherBookingCard from "../../components/booking/TeacherBookingCard.tsx";
import TeacherSubscriptionCard from "../../components/subscription/TeacherSubscriptionCard.tsx";
import DashboardEmptyState from "../../components/dashboard/DashboardEmptyState.tsx";
import { Tabs } from "../../components/Tabs.tsx";

type BookingSortBy = "createdAt" | "timeslot";
type SortDir = "asc" | "desc";

interface SortOption<T extends string> {
  value: `${T}-${SortDir}`;
  label: string;
}

const bookingSortOptions: SortOption<BookingSortBy>[] = [
  { value: "createdAt-desc", label: "Created: Newest first" },
  { value: "createdAt-asc", label: "Created: Oldest first" },
  { value: "timeslot-asc", label: "Lesson: Soonest first" },
  { value: "timeslot-desc", label: "Lesson: Latest first" },
];

const subscriptionSortOptions: SortOption<"createdAt">[] = [
  { value: "createdAt-desc", label: "Created: Newest first" },
  { value: "createdAt-asc", label: "Created: Oldest first" },
];

function sortByEarliestSlot(
  bookings: BookingResponseDto[],
): BookingResponseDto[] {
  return [...bookings].sort((a, b) => {
    const aEarliest = Math.min(
      ...a.timeSlots.map((s) => new Date(s.from).getTime()),
    );
    const bEarliest = Math.min(
      ...b.timeSlots.map((s) => new Date(s.from).getTime()),
    );
    return aEarliest - bEarliest;
  });
}

function SortSelect<T extends string>({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (val: string) => void;
  options: SortOption<T>[];
}) {
  const isAsc = value.endsWith("-asc");
  return (
    <div className="flex items-center gap-1.5">
      {isAsc ? (
        <ArrowUpAZ className="text-soluud-text-tertiary h-3.5 w-3.5 shrink-0" />
      ) : (
        <ArrowDownAZ className="text-soluud-text-tertiary h-3.5 w-3.5 shrink-0" />
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-soluud-border bg-soluud-background-card text-soluud-text-secondary focus:ring-soluud-primary cursor-pointer rounded-lg border px-2 py-1 text-xs focus:ring-1 focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TeacherDashboard() {
  const [acceptingBookingId, setAcceptingBookingId] = useState("");
  const [acceptingSubscriptionId, setAcceptingSubscriptionId] = useState("");
  const [bookingSort, setBookingSort] = useState("createdAt-desc");
  const [subscriptionSort, setSubscriptionSort] = useState("createdAt-desc");

  const [bookingSortBy, bookingSortDir] = bookingSort.split("-") as [
    BookingSortBy,
    SortDir,
  ];
  const [, subscriptionSortDir] = subscriptionSort.split("-") as [
    "createdAt",
    SortDir,
  ];

  // Pending items — unpaginated, filtered client-side
  const { data: allTeacherBookings, refetch: refetchAllBookings } =
    useTeacherBookingsQuery();
  const { data: allTeacherSubscriptions, refetch: refetchAllSubscriptions } =
    useTeacherSubscriptionsQuery();

  // Paginated main lists — all statuses, sorted from backend
  const bookingsQuery = useTeacherBookingsDashboardQuery(
    bookingSortBy,
    bookingSortDir,
  );
  const subscriptionsQuery =
    useTeacherSubscriptionsDashboardQuery(subscriptionSortDir);

  const { mutate: acceptBooking, isPending: isAccepting } =
    useAcceptBookingMutation();
  const { mutate: acceptSubscription, isPending: isAcceptingSubscription } =
    useAcceptSubscriptionMutation();

  const pendingBookings = useMemo<BookingResponseDto[]>(
    () =>
      sortByEarliestSlot(
        (allTeacherBookings || []).filter((b) => b.status === "CREATED"),
      ),
    [allTeacherBookings],
  );

  const pendingSubscriptions = useMemo<Subscription[]>(
    () =>
      (allTeacherSubscriptions || []).filter((s) => s.status === "INCOMPLETE"),
    [allTeacherSubscriptions],
  );

  const allBookings =
    bookingsQuery.data?.pages.flatMap((p) => p.bookings) ?? [];
  const bookingPages = bookingsQuery.data?.pages;
  const totalBookings =
    bookingPages != null && bookingPages.length > 0
      ? bookingPages[bookingPages.length - 1].total
      : allBookings.length;

  const allSubscriptions =
    subscriptionsQuery.data?.pages.flatMap((p) => p.subscriptions) ?? [];
  const subscriptionPages = subscriptionsQuery.data?.pages;
  const totalSubscriptions =
    subscriptionPages != null && subscriptionPages.length > 0
      ? subscriptionPages[subscriptionPages.length - 1].total
      : allSubscriptions.length;

  const handleAcceptBooking = (bookingId: string) => {
    setAcceptingBookingId(bookingId);
    acceptBooking(bookingId, {
      onSuccess: () =>
        Promise.all([refetchAllBookings(), bookingsQuery.refetch()]).catch(
          console.error,
        ),
      onSettled: () => setAcceptingBookingId(""),
    });
  };

  const handleAcceptSubscription = (subscriptionId: string) => {
    setAcceptingSubscriptionId(subscriptionId);
    acceptSubscription(subscriptionId, {
      onSuccess: () =>
        Promise.all([
          refetchAllSubscriptions(),
          subscriptionsQuery.refetch(),
        ]).catch(console.error),
      onSettled: () => setAcceptingSubscriptionId(""),
    });
  };

  const isLoading = bookingsQuery.isLoading || subscriptionsQuery.isLoading;

  if (isLoading) {
    return <LoadingSpinner loadingText={t`Loading...`} />;
  }

  const hasPendingBookings = pendingBookings.length > 0;
  const hasPendingSubscriptions = pendingSubscriptions.length > 0;
  const hasPending = hasPendingBookings || hasPendingSubscriptions;

  return (
    <Container>
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* Pending items — top on mobile, right sidebar on desktop */}
        {hasPending && (
          <section className="flex flex-col gap-6 lg:order-last lg:w-80 lg:shrink-0">
            {hasPendingBookings && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <h2 className="text-soluud-text-primary text-lg font-semibold">
                    <Trans>Pending Bookings</Trans>
                    <span className="ml-1.5 text-sm font-normal text-amber-700">
                      ({pendingBookings.length})
                    </span>
                  </h2>
                </div>
                <div className="space-y-3">
                  {pendingBookings.map((booking) => (
                    <PendingBookingCard
                      key={booking.id}
                      booking={booking}
                      onAccept={() => handleAcceptBooking(booking.id)}
                      isAccepting={
                        isAccepting && acceptingBookingId === booking.id
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {hasPendingSubscriptions && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Repeat className="h-5 w-5 text-amber-600" />
                  <h2 className="text-soluud-text-primary text-lg font-semibold">
                    <Trans>Pending Subscriptions</Trans>
                    <span className="ml-1.5 text-sm font-normal text-amber-700">
                      ({pendingSubscriptions.length})
                    </span>
                  </h2>
                </div>
                <div className="space-y-3">
                  {pendingSubscriptions.map((subscription) => (
                    <PendingSubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                      onAccept={() => handleAcceptSubscription(subscription.id)}
                      isAccepting={
                        isAcceptingSubscription &&
                        acceptingSubscriptionId === subscription.id
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Main content — tabs for bookings and subscriptions */}
        <div className="min-w-0 flex-1">
          <Tabs
            paramKey="tab"
            defaultValue="bookings"
            rightSlot={(activeTab) =>
              activeTab === "bookings" ? (
                <SortSelect
                  value={bookingSort}
                  onChange={setBookingSort}
                  options={bookingSortOptions}
                />
              ) : (
                <SortSelect
                  value={subscriptionSort}
                  onChange={setSubscriptionSort}
                  options={subscriptionSortOptions}
                />
              )
            }
          >
            <Tabs.Tab
              value="bookings"
              label={
                <span className="flex items-center gap-1.5">
                  <Calendar1 className="h-4 w-4" />
                  <Trans>Bookings</Trans>
                  <span className="text-soluud-text-secondary text-xs font-normal">
                    ({totalBookings})
                  </span>
                </span>
              }
            >
              {allBookings.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {allBookings.map((booking) => (
                    <TeacherBookingCard key={booking.id} booking={booking} />
                  ))}
                  {bookingsQuery.hasNextPage && (
                    <button
                      onClick={() => bookingsQuery.fetchNextPage()}
                      disabled={bookingsQuery.isFetchingNextPage}
                      className="text-soluud-primary hover:text-soluud-primary/80 mt-2 flex items-center gap-1.5 self-start text-sm font-medium transition-colors disabled:opacity-60"
                    >
                      {bookingsQuery.isFetchingNextPage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <Trans>Load more</Trans>
                    </button>
                  )}
                </div>
              ) : (
                <DashboardEmptyState
                  icon={Calendar1}
                  message={<Trans>No bookings yet.</Trans>}
                />
              )}
            </Tabs.Tab>

            <Tabs.Tab
              value="subscriptions"
              label={
                <span className="flex items-center gap-1.5">
                  <CalendarSync className="h-4 w-4" />
                  <Trans>Subscriptions</Trans>
                  <span className="text-soluud-text-secondary text-xs font-normal">
                    ({totalSubscriptions})
                  </span>
                </span>
              }
            >
              {allSubscriptions.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {allSubscriptions.map((subscription) => (
                    <TeacherSubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                    />
                  ))}
                  {subscriptionsQuery.hasNextPage && (
                    <button
                      onClick={() => subscriptionsQuery.fetchNextPage()}
                      disabled={subscriptionsQuery.isFetchingNextPage}
                      className="text-soluud-primary hover:text-soluud-primary/80 mt-2 flex items-center gap-1.5 self-start text-sm font-medium transition-colors disabled:opacity-60"
                    >
                      {subscriptionsQuery.isFetchingNextPage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <Trans>Load more</Trans>
                    </button>
                  )}
                </div>
              ) : (
                <DashboardEmptyState
                  icon={CalendarSync}
                  message={<Trans>No subscriptions yet.</Trans>}
                />
              )}
            </Tabs.Tab>
          </Tabs>
        </div>
      </div>
    </Container>
  );
}

export default TeacherDashboard;
